<template>
  <b-modal
    id="add-new-card"
    ref="add-new-card"
    body-class="p-10"
    centered
    title="Adding New Credit Card"
    @ok="submitCard"
    @show="setModalData"
    @hidden="resetModal"
  >
    <b-overlay
      id="overlay-new-card"
      :show="isModalLoading"
      variant="white"
      opacity="1"
      rounded="sm"
    >
      <!-- Credit Card Cardknox Form -->
      <credit-card-cardknox-template
        v-if="isCardknox"
        ref="cardknoxTemplate"
        :i-fields-options="iFieldsOptions"
      />

      <!-- Credit Card Stripe Form -->
      <credit-card-stripe-template
        v-if="isStripe"
        :has-card-error="hasCardError"
        :stripe-item-i-d="stripeItemID"
      />


      <!-- Billing Address Form -->
      <billing-address-template
        :editing-mode="false"
        :creating-mode="true"
      />

    </b-overlay>
    <template #modal-footer="{ cancel, ok }">
      <div class="w-100 d-flex justify-content-between">
        <b-button
          class="border border-primary"
          variant="outline-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="ok()"
          :disabled="hasCardError"
        >
          Save
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { getUserData } from '@/auth/utils'
import { mapGetters } from 'vuex'
import store from '@/store'

import { BModal, BButton, BOverlay } from 'bootstrap-vue'
import CreditCardStripeTemplate from '@/components/site/CreditCardStripeTemplate.vue'
import uniqueId from 'lodash/uniqueId'
import CreditCardCardknoxTemplate from '@/components/site/CreditCardCardknoxTemplate.vue'
import BillingAddressTemplate from '@/components/site/BillingAddressTemplate.vue'
import cardknoxMixin from '@core/mixins/cardknoxMixin.js'
import stripeMixin from '@core/mixins/stripeMixin'

export default {
  mixins: [stripeMixin, cardknoxMixin],
  components: {
    BillingAddressTemplate,
    CreditCardCardknoxTemplate,
    CreditCardStripeTemplate,
    BModal,
    BButton,
    BOverlay,
  },
  data() {
    return {
      showBillingAddress: true,

      stripeItemID: '',

      userId: getUserData().id,

      cardToken: null,
    }
  },
  computed: {
    ...mapGetters({
      publishableKey: 'paymentSettings/getStripePublishKey',
      isCardknox: 'paymentSettings/getIsCardknox',
      isStripe: 'paymentSettings/getIsStripe',
      iFieldsOptions: 'paymentSettings/getCardknoxIFieldsOptions',
      billingAddressFormData: 'formValidation/getBillingAddressFormData',
      billingAddressRequiredFields: 'formValidation/getBillingAddressRequiredFields',
    }),
    isModalLoading: {
      get: function () {
        return store.getters['paymentPortal/getIsModalLoaidng']
      },
      set: function (val) {
        store.commit('paymentPortal/SET_MODAL_LOADING_STATE', val)
      },
    },
    cardholderName() {
      return this.$store.getters['formValidation/getCreditCardField']('cardholder_name')
    },
    cardExpiry() {
      return this.$store.getters['formValidation/getCreditCardField']('card_expiry')
    },
    hasCardError() {
      return !!this.$store.getters['formValidation/getCreditCardError']('card_number')
        || !!this.$store.getters['formValidation/getCreditCardError']('card_expiry')
        || !!this.$store.getters['formValidation/getCreditCardError']('card_cvc')
    },
  },
  beforeDestroy () {
    this.clearForms()
  },
  methods: {
    async clearForms() {
      this.$store.commit('formValidation/resetBillingAddressForm')
      this.$store.commit('formValidation/resetCreditCardFormData')
      this.$store.commit('formValidation/resetACHFormData')

      if (this.isStripe) {
        await this.destroyMountedForm()
      }
    },
    async setModalData() {
      if (this.isStripe) {
        this.stripeItemID = uniqueId()
        await this.initStripe(this.publishableKey, this.stripeItemID)
      }
    },
    async validateForm() {
      this.$store.commit('formValidation/resetAllErrors')

      let isValid = true

      // helper: commit validation error
      const setError = (module, field, value) => {
        this.$store.commit(`formValidation/${module}`, { field, value })
        if (value) isValid = false
      }

      // helper: required check
      const requireField = (value) => Boolean(value && value.trim())

      // ------------ BILLING ADDRESS -------------
      if (this.showBillingAddress) {
        const form = this.billingAddressFormData
        const required = this.billingAddressRequiredFields

        required.forEach(field => {
          const ok = requireField(form[field])
          setError('setBillingAddressFormError', field, !ok)
        })
      }

      // ------------ CARDHOLDER NAME -------------
      const ok = requireField(this.cardholderName)
      setError('setCreditCardFormError', 'cardholder_name', !ok)

      // ------------ STRIPE -------------
      if (this.isStripe) {
        const stripe = [
          { el: this.cardNumberStripeElement, field: 'card_number', msg: 'Card number is required' },
          { el: this.cardExpiryStripeElement, field: 'card_expiry', msg: 'Expiration date is required' },
          { el: this.cardCvcStripeElement, field: 'card_cvc', msg: 'CVC is required' }
        ]

        stripe.forEach(item => {
          const ok = item.el?._complete === true
          setError('setCreditCardFormError', item.field, ok ? false : item.msg)
        })
      }

      // ------------ CARDKNOX CC -------------
      if (this.isCardknox) {
        const expiryError = await store.dispatch('formValidation/validateCardknoxExpiry', this.cardExpiry)
        setError('setCreditCardFormError', 'card_expiry', expiryError)
      }

      return isValid
    },
    async submitCard(bvModalEvt) {
      bvModalEvt.preventDefault()

      // Step 1: Validate form
      const ok = await this.validateForm()
      if (!ok) return

      if (this.isCardknox) {
        await this.handleCardknoxCCPayment()
      }
      if (this.isStripe) {
        await this.handleStripePayment()
      }
    },
    async handleStripePayment() {
      const token = await this.fetchStripeCardToken(this.stripeItemID)

      token.gateway = 'stripe'
      token.billingAddress = this.billingAddressFormData
      token.card.cardholder_name = this.cardholderName

      await this.saveTokenToServer(token)
    },
    async saveTokenToServer(token) {
      token.user_id = this.userId

      this.isModalLoading = true
      await store.dispatch('cards/createNewCreditCard', token)
      await store.dispatch('cards/fetchUserCards')
      this.isModalLoading = false
      this.$refs['add-new-card'].hide()
    },
    async handleCardknoxCCPayment() {
      if (!this.$refs.cardknoxTemplate.$refs.ifieldCardRef) {
        return
      }

      await this.fetchCardknoxCardToken()

      if (!this.cardToken || !this.cardExpiry) return

      try {
        const cardNumber = this.cardToken.split(';')[0]
        const match = this.cardToken.match(/cc_(.*?)_keyed/)
        const cardType = match ? match[1] : 'unknown'

        const [month, year] = this.cardExpiry.split('/');

        await this.saveTokenToServer({
          id: this.cardToken,
          card: {
            exp_month: month,
            exp_year: year,
            last4: cardNumber.slice(-4),
            brand: cardType,
            cardholder_name: this.cardholderName,
          },
          billingAddress: this.billingAddressFormData,
          gateway: 'cardknox',
        })
      } catch (error) {
        // document.getElementById('#' + this.cardknoxItemID + 'card-error').innerHTML = error.message
        console.error('Ошибка токенизации:', error)
      }
    },
    resetModal() {
      this.clearForms()
    },
  }
}
</script>

<style>

</style>